import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Avatar, Box, Button, Card, CardContent, CardHeader, Typography } from '@mui/material'
import { AppContext } from '../../../App'
import { ChartEvents } from '../chart.events'
import { ExtraWideTooltip } from '../../custom'
import Contract from '../../../assets/icons/contractSwitchIcon'
import { Type } from '../../../proto/generated/corporate-action'
import {
    getCompanyEventColor,
    getCompanyEventPurposeDisplayName,
} from '../drawingTools/companyEventTool/companyEventTool.helpers'
import useStyles from './styles'
import { dividendTypeMap } from '../../../assets/constants'
import { useDispatch, useSelector } from 'react-redux'
import commonActions from '../../../actions/common.actions'
import { WidgetListItems } from '../../../util/sidebar'
import { LightningBoltIcon } from '../drawingTools/newsArticleTool/lightningIcon'
import { selectIsEmbedded } from '../../../selectors/device'
import { callNative } from '../../../util/nativeBridge'

export type ContractsTooltipProps = {
    chartId: string;
};
const ContractsTooltip = ({ chartId }: ContractsTooltipProps) => {
    const appContext = useContext(AppContext)
    const [showFullDetails, setShowFullDetails] = useState(false)
    const toggleDetails = () => setShowFullDetails(!showFullDetails)
    const [isTruncated, setIsTruncated] = useState(false)
    const textRef = useRef<HTMLDivElement | null>(null)
    const chart = appContext.getChart(chartId)
    const priceMultiplier = chart.securities?.[0]?.priceMultiplier ?? 100
    const [contractSwitchData, setContractSwitchData] = useState<any>()
    const [corporateActionsData, setCorporateActionsData] = useState<any>()
    const [companyEventsData, setCompanyEventsData] = useState<any>()
    const [newsArticleData, setNewsArticleData] = useState<any>()
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const isEmbedded = useSelector(selectIsEmbedded)
    
    // Helper functions
    const getDisplayName = (type: Type): string => {
        switch (type) {
            case Type.BONUS:
                return 'Bonus'
            case Type.SPECIAL_DIVIDEND:
                return 'Dividend'
            case Type.FINAL_DIVIDEND:
                return 'Dividend'
            case Type.RIGHTS:
                return 'Rights'
            case Type.INTERIM_DIVIDEND:
                return 'Dividend'
            case Type.SPLIT:
                return 'Split'
        }
        return 'Corporate Action'
    }
    
    const getProviderNameFromProviderCode = (providerCode: string): string => {
        switch (providerCode) {
            case 'MONEY_CONTROL':
                return 'Money Control'
            case 'ECONOMIC_TIMES':
                return 'Economic Times'
            case 'NDTV_PROFIT':
                return 'NDTV Profit'
        }
        return providerCode
    }
    
    const onDrawingHovered = useCallback((event: CustomEvent) => {
        // The drawing tools publish `null` to dismiss (deselect / on every visible-range
        // change). Natively the tooltip is a one-shot sheet, so there is nothing to
        // dismiss; the web branches below already accept null via their setters.
        if (isEmbedded && event.detail == null) return
        if (event.type === ChartEvents.CONTRACT_SWITCH_CLICKED) {
            if (isEmbedded) {
                const contractSwitchData = {
                    time: event.detail.time || Date.now(),
                    fromContract: event.detail.fromContract || '',
                    toContract: event.detail.toContract || ''
                }
                callNative('onContractSwitchIconClicked', JSON.stringify(contractSwitchData))
            } else {
                setContractSwitchData(event.detail)
            }
        } else if (event.type === ChartEvents.CORPORATE_CLICKED) {
            if (isEmbedded) {
                const corporateActionData = [{
                    type: event.detail.dataType || 0,
                    exDate: event.detail.exDate || 0,
                    recordDate: event.detail.recordDate || 0,
                    announcementDate: event.detail.announcementDate || 0,
                    dividendPercentage: event.detail.dividendPercentage || 0.0,
                    oldFaceValue: event.detail.oldFaceValue || 0.0,
                    newFaceValue: event.detail.newFaceValue || 0.0,
                    ratioNumerator: event.detail.ratioNumerator || 0.0,
                    ratioDenominator: event.detail.ratioDenominator || 0.0,
                    rightsPremium: event.detail.rightsPremium || 0.0,
                    dividendAmount: event.detail.dividendAmount || 0.0,
                    primaryCode: event.detail.primaryCode || '',
                    companyCode: event.detail.companyCode || '',
                    primaryExchange: event.detail.primaryExchange || 0
                }]
                callNative('onCorporateActionIconClicked', JSON.stringify(corporateActionData))
            } else {
                setCorporateActionsData(event.detail)
            }
        } else if (event.type === ChartEvents.COMPANY_EVENT_CLICKED) {
            if (isEmbedded) {
                const eventData = [{
                    primaryCode: event.detail.primaryCode || '',
                    purpose: event.detail.purpose || [],
                    details: event.detail.details || '',
                    date: event.detail.date || '',
                    companyCode: event.detail.companyCode || '',
                    primaryExchange: event.detail.primaryExchange || 0,
                    displayPurpose: event.detail.displayPurpose || event.detail.purpose?.map((code: number) => 
                        getCompanyEventPurposeDisplayName(code)).join(', ') || ''
                }]
                callNative('onEventIconClicked', JSON.stringify(eventData))
            } else {
                setCompanyEventsData(event.detail)
            }
        } else if (event.type === ChartEvents.NEWS_ARTICLE_CLICKED) {
            if (isEmbedded) {
                const newsData = event.detail.articles?.map((article: any) => ({
                    providerCode: article.providerCode || '',
                    title: article.title || '',
                    summary: article.summary || article.title || '',
                    link: article.link || null,
                    imageUrl: article.imageUrl || null,
                    created: article.created || Date.now(),
                    primaryCompanyIdentifiers: article.primaryCompanyIdentifiers || []
                })) || []
                callNative('onNewsIconClicked', JSON.stringify(newsData))
            } else {
                setNewsArticleData(event.detail)
            }
        }
    }, [isEmbedded, priceMultiplier])
    const data = useMemo(() => {
        return contractSwitchData ? contractSwitchData
            : corporateActionsData ? corporateActionsData
                : companyEventsData ? companyEventsData
                    : newsArticleData ? newsArticleData
                        : null
    }, [contractSwitchData, corporateActionsData, companyEventsData, newsArticleData])
    const getDisplayDate = (time: number): string => {
        return new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }).format(new Date(time))

    }
    const getColorForAction = (type: Type): string => {
        switch (type) {
            case Type.BONUS:
                return '#B8860B'
            case Type.SPECIAL_DIVIDEND:
                return '#0f89e5'
            case Type.FINAL_DIVIDEND:
                return '#0f89e5'
            case Type.RIGHTS:
                return 'rgb(171, 71, 188)'
            case Type.INTERIM_DIVIDEND:
                return '#0f89e5'
            case Type.SPLIT:
                return 'rgb(251, 140, 0)'
        }
        return '#0f89e5'
    }
    const getDisplayLetter = (type: Type): string => {
        switch (type) {
            case Type.BONUS:
                return 'B'
            case Type.FINAL_DIVIDEND:
                return 'D'
            case Type.SPECIAL_DIVIDEND:
                return 'D'
            case Type.RIGHTS:
                return 'R'
            case Type.INTERIM_DIVIDEND:
                return 'D'
            case Type.SPLIT:
                return 'S'
        }
        return 'Corporate Action'
    }

    const UpcomingEventDescription = memo(({ action }: { action: any }) => {
        useEffect(() => {
            if (textRef.current) {
                setIsTruncated(textRef.current.scrollHeight > textRef.current.clientHeight)
            }
        }, [action.details])

        const capitalizeFirstLetter = (text: string) => {
            return text ? text.charAt(0).toUpperCase() + text.slice(1) : ''
        }

        return (
            <>
                <Box className={classes.container}>
                    <Typography className={classes.label}>Purpose</Typography>
                    <Typography className={classes.value}>
                        {action.purpose.map((code: any) => getCompanyEventPurposeDisplayName(code)).join(', ')}
                    </Typography>
                </Box>
                <Box className={classes.container}>
                    <Typography className={classes.label}>Date</Typography>
                    <Typography className={classes.value}>{getDisplayDate(action.date)}</Typography>
                </Box>
                <Box className={classes.container}>
                    <Typography className={classes.label}>Details</Typography>
                    <Box className={classes.value}>
                        <Typography
                            ref={textRef}
                            fontSize="14px"
                            sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                WebkitLineClamp: showFullDetails ? 'unset' : 2,
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal'
                            }}
                        >
                            {capitalizeFirstLetter(action.details) || 'No details available'}
                        </Typography>
                        {isTruncated && !showFullDetails && (
                            <Typography
                                className={classes.clickableText}
                                onClick={toggleDetails}
                            >
                                {' '}
                                Read More
                            </Typography>
                        )}

                        {showFullDetails && (
                            <Typography
                                className={classes.clickableText}
                                onClick={toggleDetails}
                            >
                                Read Less
                            </Typography>
                        )}
                    </Box>
                </Box>
            </>
        )
    })

    useEffect(() => {
        if (chart) {
            chart.subscribeEvent(ChartEvents.CORPORATE_CLICKED, onDrawingHovered)
            chart.subscribeEvent(ChartEvents.CONTRACT_SWITCH_CLICKED, onDrawingHovered)
            chart.subscribeEvent(ChartEvents.COMPANY_EVENT_CLICKED, onDrawingHovered)
            chart.subscribeEvent(ChartEvents.NEWS_ARTICLE_CLICKED, onDrawingHovered)
        }

        return () => {
            if (chart) {
                chart.unsubscribeEvent(ChartEvents.CORPORATE_CLICKED, onDrawingHovered)
                chart.unsubscribeEvent(ChartEvents.CONTRACT_SWITCH_CLICKED, onDrawingHovered)
                chart.unsubscribeEvent(ChartEvents.COMPANY_EVENT_CLICKED, onDrawingHovered)
                chart.unsubscribeEvent(ChartEvents.NEWS_ARTICLE_CLICKED, onDrawingHovered)
            }
        }
    }, [chart])


    const ActionContent = ({ action }: { action: any }) => {
        switch (action.dataType) {
            case Type.BONUS: {
                return (<>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Bonus
                            Ratio</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'} >{`${action.ratioNumerator}:${action.ratioDenominator}`}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Ex
                            Date</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{getDisplayDate(action.exDate)}</Typography>
                    </Box>
                    {action.announcementDate !== 0 && (<Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Announcement
                            Date</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{getDisplayDate(action.announcementDate)}</Typography>
                    </Box>)}
                    {action.recordDate !== 0 &&
                        (<Box sx={{ display: 'flex', width: '100%' }}>
                            <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'}
                                fontSize={'14px'}>Record Date</Typography>
                            <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                                fontSize={'14px'}>{getDisplayDate(action.recordDate)}</Typography>
                        </Box>)}
                </>)
            }
            case Type.SPECIAL_DIVIDEND:
            case Type.FINAL_DIVIDEND:
            case Type.INTERIM_DIVIDEND: {
                return (<>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Dividend
                            Amount</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{(action.dividendAmount / priceMultiplier).toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'}
                            fontSize={'14px'}>Type</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{dividendTypeMap[Type[action.dataType].toString()]}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Ex
                            Date</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{getDisplayDate(action.exDate)}</Typography>
                    </Box>
                    {action.announcementDate !== 0 &&
                        (<Box sx={{ display: 'flex', width: '100%' }}>
                            <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'}
                                fontSize={'14px'}>Announcement Date</Typography>
                            <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                                fontSize={'14px'}>{getDisplayDate(action.announcementDate)}</Typography>
                        </Box>)}
                    {action.recordDate !== 0 &&
                        (<Box sx={{ display: 'flex', width: '100%' }}>
                            <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'}
                                fontSize={'14px'}>Record Date</Typography>
                            <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                                fontSize={'14px'}>{getDisplayDate(action.recordDate)}</Typography>
                        </Box>)}

                </>)
            }
            case Type.RIGHTS: {
                return (<>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'}
                            fontSize={'14px'}>Premium</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}> {action.rightsPremium / priceMultiplier}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Face
                            Value</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}><span style={{ paddingRight: 2 }}>₹</span>{action.newFaceValue / priceMultiplier}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Rights
                            Ratio</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{`${action.ratioNumerator}:${action.ratioDenominator}`}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Ex
                            Date</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{getDisplayDate(action.exDate)}</Typography>
                    </Box>
                    {action.announcementDate !== 0 && (<Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Announcement
                            Date</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{getDisplayDate(action.announcementDate)}</Typography>
                    </Box>)}
                    {action.recordDate !== 0 && (<Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Record
                            Date</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{getDisplayDate(action.recordDate)}</Typography>
                    </Box>)}


                </>)
            }
            case Type.SPLIT: {
                return <>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Split
                            Ratio</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{`${action.ratioNumerator}:${action.ratioDenominator}`}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Face
                            Value</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}> <span style={{ paddingRight: 2 }}>₹</span>{action.newFaceValue / priceMultiplier}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Ex
                            Date</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{getDisplayDate(action.exDate)}</Typography>
                    </Box>
                    {action.announcementDate !== 0 && (<Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'} fontSize={'14px'}>Announcement
                            Date</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{getDisplayDate(action.announcementDate)}</Typography>
                    </Box>)}
                    {action.recordDate && (<Box sx={{ display: 'flex', width: '100%' }}>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%' }} color={'textSecondary'}
                            fontSize={'14px'}>Record Date</Typography>
                        <Typography sx={{ flexGrow: 0, flexBasis: '50%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                            fontSize={'14px'}>{getDisplayDate(action.recordDate)}</Typography>
                    </Box>
                    )}
                </>
            }
        }
        return <></>
    }
    const handleShowMore = useCallback(() => {
        setNewsArticleData(null)
        commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.MARKET_NEWS)
    }, [dispatch])

    return data ? (
        data.type === 'contractSwitch' ? (
            <ExtraWideTooltip open={true} placement="top" title={(
                <Card style={{ borderLeft: '4px solid rgb(124, 77, 255)' }}>
                    <CardHeader
                        avatar={
                            <Avatar aria-label="contract-switch" style={{
                                backgroundColor: 'Transparent',
                                border: '2px solid rgb(124, 77, 255)',
                                width: 30,
                                height: 30
                            }}>
                                <Contract sx={{ fontSize: '13px', ml: '1px', color: 'rgb(124, 77, 255)' }} />
                            </Avatar>
                        }
                        title={<Typography variant="h6" fontSize={'18px'}>{'Contract Switch'}</Typography>}
                    />
                    <CardContent sx={{ minWidth: 350, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Box sx={{ display: 'flex', width: '100%' }}>
                            <Typography sx={{ flexGrow: 0, flexBasis: '40%' }} color={'textSecondary'}
                                fontSize={'14px'}>Date</Typography>
                            <Typography sx={{ flexGrow: 0, flexBasis: '60%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                                fontSize={'14px'} >{getDisplayDate(data.time)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', width: '100%' }}>
                            <Typography sx={{ flexGrow: 0, flexBasis: '40%' }} color={'textSecondary'}
                                fontSize={'14px'}>From</Typography>
                            <Typography sx={{ flexGrow: 0, flexBasis: '60%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                                fontSize={'14px'}>{data.fromContract}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', width: '100%' }}>
                            <Typography sx={{ flexGrow: 0, flexBasis: '40%' }} color={'textSecondary'}
                                fontSize={'14px'}>To</Typography>
                            <Typography sx={{ flexGrow: 0, flexBasis: '60%', fontFeatureSettings: '"tnum" on,"lnum" on' }}
                                fontSize={'14px'} >{data.toContract}</Typography>
                        </Box>
                    </CardContent>
                </Card>
            )} sx={{ margin: '0px !important', padding: '0px !important' }}>
                <Box sx={{ position: 'absolute', left: data.x, top: data.y }} />
            </ExtraWideTooltip>
        ) : data.type === 'corporateAction' ? (
            <ExtraWideTooltip open={true} title={(
                <Card style={{ borderLeft: `4px solid ${getColorForAction(data.dataType)}` }}>
                    <CardContent sx={{ minWidth: 350, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <CardHeader
                            style={{ marginBottom: '8px', padding: '0' }}
                            avatar={
                                <Avatar aria-label="corporate-action"
                                    style={{
                                        backgroundColor: 'Transparent',
                                        border: `2px solid ${getColorForAction(data.dataType)}`,
                                        width: 24, height: 24,
                                        color: `${getColorForAction(data.dataType)}`
                                    }}
                                >
                                    <span style={{
                                        color: `${getColorForAction(data.dataType)}`,
                                        fontSize: '13px',
                                        fontWeight: 500
                                    }}>
                                        {getDisplayLetter(data.dataType)}
                                    </span>
                                </Avatar>
                            }
                            title={<Typography variant="h6"
                                fontSize={'18px'}>{getDisplayName(data.dataType)}</Typography>}
                        />
                        <ActionContent action={data} />
                    </CardContent>
                </Card>
            )} sx={{ margin: 0, padding: 0 }}>
                <Box sx={{ position: 'absolute', left: data.x, top: data.y }} />
            </ExtraWideTooltip>
        ) : data.type === 'companyEvent' ? (
            <ExtraWideTooltip open={true} title={(
                <Card
                    style={{
                        borderLeft: `4px solid ${getCompanyEventColor(data.purpose.map((code: number) => getCompanyEventPurposeDisplayName(code).toLowerCase()))}`
                    }}
                >
                    <CardContent
                        sx={{
                            minWidth: 350,
                            maxHeight: 250,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }}
                    >
                        <CardHeader
                            style={{ marginBottom: '8px', padding: '0' }}
                            avatar={
                                <Avatar
                                    aria-label="company-event"
                                    style={{
                                        backgroundColor: 'Transparent',
                                        border: `2px solid ${getCompanyEventColor(data.purpose.map((code: number) => getCompanyEventPurposeDisplayName(code).toLowerCase()))}`,
                                        width: 24, height: 24,
                                        color: getCompanyEventColor(data.purpose.map((code: number) => getCompanyEventPurposeDisplayName(code).toLowerCase()))
                                    }}
                                >
                                    <span style={{
                                        color: getCompanyEventColor(data.purpose.map((code: number) => getCompanyEventPurposeDisplayName(code).toLowerCase())),
                                        fontSize: '13px',
                                        fontWeight: 500
                                    }}>
                                        {'E'}
                                    </span>
                                </Avatar>
                            }
                            title={<Typography variant="h6" fontSize={'18px'}>Event details</Typography>}
                        />
                        <UpcomingEventDescription action={data} />
                    </CardContent>
                </Card>
            )}
                sx={{ maxWidth: 400, margin: 0, padding: 0 }}
            >
                <Box sx={{ position: 'absolute', left: data.x, top: data.y }} />
            </ExtraWideTooltip>
        ) : data.type === 'newsArticle' ? (
            <ExtraWideTooltip
                open={true}
                title={
                    <Card
                        style={{
                            borderLeft: `4px solid #8B5CF6`
                        }}
                    >
                        <CardContent sx={{ minWidth: 250, maxWidth: 350, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <CardHeader
                                style={{ marginBottom: '8px', padding: '0' }}
                                avatar={
                                    <Avatar
                                        aria-label="news-article"
                                        style={{
                                            backgroundColor: 'Transparent',
                                            border: `2px solid #8B5CF6`,
                                            width: 24,
                                            height: 24,
                                            color: '#8B5CF6'
                                        }}
                                    >
                                        <LightningBoltIcon size={20} color="#8B5CF6" />
                                    </Avatar>
                                }
                                title={
                                    <Typography variant="h6" fontSize={'18px'}>
                                        News
                                    </Typography>
                                }
                            />
                            {data.articles?.slice(0, 3).map((article: any, index: number) => (
                                <Box key={`${article.created}-${article.title}`}>
                                    {index > 0 && (
                                        <Box
                                            sx={{
                                                borderTop: '1px solid',
                                                borderColor: 'divider',
                                                my: 1
                                            }}
                                        />
                                    )}
                                    <Box sx={{ display: 'flex', width: '100%', mb: 0.5 }}>
                                        <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                                            {new Date(article.created * 1000).toLocaleString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit',
                                                hour12: false
                                            })}{' '}
                                            • {getProviderNameFromProviderCode(article.providerCode)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', width: '100%' }}>
                                        <Typography
                                            component={article.link ? 'a' : 'div'}
                                            href={article.link || undefined}
                                            target={article.link ? '_blank' : undefined}
                                            rel={article.link ? 'noopener noreferrer' : undefined}
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 'normal',
                                                wordBreak: 'break-word',
                                                whiteSpace: 'normal',
                                                textDecoration: 'none',
                                                color: article.link ? 'primary.main' : 'text.primary',
                                                cursor: article.link ? 'pointer' : 'default',
                                                '&:hover': article.link ? { textDecoration: 'underline' } : {}
                                            }}
                                        >
                                            {article.title}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                            {(data.articles?.length > 3) && (
                                <Box sx={{ mt: 1 }}>
                                    <Button variant="text" size="small" onClick={handleShowMore} sx={{ fontSize: '12px', padding: '2px 8px' }}>
                                        Show More
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                }
                sx={{ margin: 0, padding: 0 }}
            >
                <Box sx={{ position: 'absolute', left: data.x, top: data.y }} />
            </ExtraWideTooltip>
        ) : null
    ) : null
}

export default memo(ContractsTooltip)
